export type ProjectScreen =
  | 'coverage'
  | 'reconciliation'
  | 'transactions'
  | 'chat'
  | 'analysis'

export type StepAction =
  | { kind: 'create-project'; label: string }
  | { kind: 'add-statements'; label: string }
  | { kind: 'project-screen'; label: string; screen: ProjectScreen }

export interface StepClip {
  // A short muted loop of the real app. Null until the clip is rendered.
  video: string | null
  // The clip's first frame, shown while it loads and to anyone who asked for less motion.
  still: string
  // The scene name in growth/video/DEMO_SCENES.md this clip is cut from.
  scene: string
}

export interface GettingStartedStep {
  key: string
  title: string
  description: string
  clip: StepClip
  action: StepAction | null
  upgradeNote?: string
}

const createProject: GettingStartedStep = {
  key: 'create-project',
  title: 'Create a project and add statements',
  description:
    'Name the project. Drag in the PDF statements. We read every statement range and every transaction.',
  clip: {
    video: '/getting-started/step-create-project.mp4',
    still: '/getting-started/step-create-project.webp',
    scene: 'FirstProject',
  },
  action: { kind: 'create-project', label: 'Create a project' },
}

// A single-project signup starts with its project already made and cannot
// create another, so its first step is the upload. The key is shared so the
// step is done the same way: once the project has processed statements.
const addStatements: GettingStartedStep = {
  ...createProject,
  title: 'Add your statements',
  description:
    'Your project is ready. Drag in the PDF statements. We read every statement range and every transaction.',
  action: { kind: 'add-statements', label: 'Add statements' },
}

const checkCoverage: GettingStartedStep = {
  key: 'coverage',
  title: 'Check your coverage',
  description:
    'Coverage shows every account and every month you have, and any month that is missing. A green month means its transactions add up to the balances the bank printed.',
  clip: {
    video: '/getting-started/step-coverage.mp4',
    still: '/getting-started/step-coverage.webp',
    scene: 'Coverage',
  },
  action: {
    kind: 'project-screen',
    label: 'Open Insights',
    screen: 'coverage',
  },
}

const checkAMonth: GettingStartedStep = {
  key: 'check-a-month',
  title: "Check a month against the bank's pages",
  description:
    'Open any month next to the page the bank printed. If a number was read wrong, fix it in place.',
  clip: {
    video: '/getting-started/step-check-a-month.mp4',
    still: '/getting-started/step-check-a-month.webp',
    scene: 'Correction',
  },
  action: {
    kind: 'project-screen',
    label: 'Open reconciliation',
    screen: 'reconciliation',
  },
}

const checkRowsAgainstPages: GettingStartedStep = {
  key: 'rows-against-pages',
  title: 'Check your rows against the pages',
  description:
    'Coverage shows every account and every statement range you have. Open any month side by side with the page the bank printed. If a number was read wrong, fix it in place.',
  clip: {
    video: null,
    still: '/getting-started/step-check-a-month.webp',
    scene: 'CoverageWithoutReconciliation',
  },
  action: {
    kind: 'project-screen',
    label: 'Open Insights',
    screen: 'coverage',
  },
  upgradeNote: 'Reconciliation adds a monthly verdict for every account.',
}

const workTransactions: GettingStartedStep = {
  key: 'transactions',
  title: 'Work the transactions',
  description:
    'Search, sort, and see the count and total for any search. Click a row to see the page the bank printed. If there are checks, each one shows the payee, amount and image.',
  clip: {
    video: '/getting-started/step-transactions.mp4',
    still: '/getting-started/step-transactions.webp',
    scene: 'Transactions + Checks',
  },
  action: {
    kind: 'project-screen',
    label: 'Open Transactions',
    screen: 'transactions',
  },
}

const askDaystrom: GettingStartedStep = {
  key: 'ask-daystrom',
  title: 'Ask Daystrom',
  description:
    'Ask anything about the money. Daystrom answers from the rows in your files and can follow money that went to outside accounts.',
  clip: {
    video: '/getting-started/step-ask-daystrom.mp4',
    still: '/getting-started/step-ask-daystrom.webp',
    scene: 'FlowOfFunds',
  },
  action: { kind: 'project-screen', label: 'Open chat', screen: 'chat' },
}

const generateReport: GettingStartedStep = {
  key: 'report',
  title: 'Generate the report',
  description:
    'Pick the sections and focus areas, add case details, and get a forensic report with next steps.',
  clip: {
    video: '/getting-started/step-report.mp4',
    still: '/getting-started/step-report.webp',
    scene: 'Report',
  },
  action: {
    kind: 'project-screen',
    label: 'Open AI Analysis',
    screen: 'analysis',
  },
}

// Reconciliation is premium. Without it there is no red or green month to fix,
// so the coverage and fix steps fold into one.
export const gettingStartedSteps = (
  reconciliationEntitled: boolean,
  singleProject = false,
): GettingStartedStep[] => {
  const firstStep = singleProject ? addStatements : createProject
  return reconciliationEntitled
    ? [
        firstStep,
        checkCoverage,
        checkAMonth,
        workTransactions,
        askDaystrom,
        generateReport,
      ]
    : [
        firstStep,
        checkRowsAgainstPages,
        workTransactions,
        askDaystrom,
        generateReport,
      ]
}

// The part of a project URL after `/projects/<slug>` for each screen. Chat has
// none: it is a panel on the project page, not a screen of its own.
const SCREEN_PATHS: Record<ProjectScreen, string> = {
  coverage: '/insights/coverage',
  reconciliation: '/insights/reconciliation',
  transactions: '/transactions',
  analysis: '/analysis',
  chat: '',
}

const PROJECT_PATH = /^\/projects\/[^/]+(\/.*)?$/

export const projectScreenPath = (screen: ProjectScreen, slug: string) =>
  `/projects/${slug}${SCREEN_PATHS[screen]}`

export const screenOfPath = (pathname: string): ProjectScreen | null => {
  const rest = pathname.match(PROJECT_PATH)?.[1]
  if (!rest) return null
  const screens = Object.keys(SCREEN_PATHS) as ProjectScreen[]
  return (
    screens.find(
      (screen) =>
        SCREEN_PATHS[screen] !== '' &&
        (rest === SCREEN_PATHS[screen] ||
          rest.startsWith(`${SCREEN_PATHS[screen]}/`)),
    ) ?? null
  )
}

export const stepForScreen = (
  steps: GettingStartedStep[],
  screen: ProjectScreen,
) =>
  steps.find(
    (step) =>
      step.action?.kind === 'project-screen' && step.action.screen === screen,
  ) ?? null

export const nextOpenStep = (
  steps: GettingStartedStep[],
  doneStepKeys: string[],
) => steps.find((step) => !doneStepKeys.includes(step.key)) ?? null

export const stepCountWord = (count: number) => (count === 5 ? 'Five' : 'Six')
